// PhaseEndPanel — the end-of-phase "Time's Up!" + "Complete" affordance.
//
// Shown at the end of a timed phase (README §6.7, DECISIONS §F.3). It pairs a
// calm "<Phase> Time's Up!" heading with a "<Phase> Complete" confirmation
// checkbox. The checkbox is disabled until every task in the phase has been
// checked off (its outstanding panel is empty), so the cook can't advance
// past unfinished work.
//
// `timesUp`: true once the phase timer has expired. Prep phases may also reach
// this panel by finishing EARLY (every task checked before the clock is up);
// in that case `timesUp` is false and only the "<Phase> Complete" block shows
// — no "Time's Up!" (LiveCook never finishes early — DECISIONS §F.3).
//
// Two layouts:
//   - 'horizontal' (full-width single phase / LiveCook): Time's Up! on the
//     left third, Complete on the right two-thirds (Complete fills the width
//     when there is no Time's Up! block).
//   - 'stacked' (each half of the split prep screen): Time's Up! on top,
//     Complete underneath.

import { Checkbox } from '../design/components';
import { theme } from '../design/theme';

export interface PhaseEndPanelProps {
  /** 'PreCook' | 'PrepCook' | 'LiveCook'. */
  phaseLabel: string;
  layout: 'horizontal' | 'stacked';
  /** True when the phase timer has expired (show the "Time's Up!" block). */
  timesUp: boolean;
  /** Whether the Complete checkbox is ticked. */
  checked: boolean;
  /** True once every task in the phase is checked — gates the checkbox. */
  canComplete: boolean;
  /**
   * Which side of the panel the Complete checkbox sits on, mirroring the
   * cook's handedness so it falls under the working hand (README §6.7,
   * issue: checkbox follows the selected hand). 'left' (default) puts the
   * checkbox before the label; 'right' after it.
   */
  checkboxSide?: 'left' | 'right';
  onToggle: () => void;
}

export function PhaseEndPanel({
  phaseLabel,
  layout,
  timesUp,
  checked,
  canComplete,
  checkboxSide = 'left',
  onToggle,
}: PhaseEndPanelProps) {
  const horizontal = layout === 'horizontal';
  const checkboxState = checked ? 'checked' : canComplete ? 'unchecked' : 'disabled';

  return (
    <div
      role="group"
      aria-label={timesUp ? `${phaseLabel} Time's Up` : `${phaseLabel} Complete`}
      style={{
        display: 'flex',
        flexDirection: horizontal ? 'row' : 'column',
        alignItems: 'stretch',
        gap: '1rem',
        width: '100%',
      }}
    >
      {/* Time's Up! — 1/3 in horizontal, full-width on top when stacked.
          Omitted on an early finish (timer not yet expired). */}
      {timesUp && (
        <div
          style={{
            flex: horizontal ? '1 1 0' : '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem 1rem',
            background: theme.bg.raised,
            borderRadius: '12px',
            textAlign: 'center',
          }}
        >
          <span style={{ fontSize: '1.6rem', fontWeight: 700, color: theme.accent.action }}>
            {phaseLabel} Time&rsquo;s Up!
          </span>
        </div>
      )}

      {/* Complete — 2/3 in horizontal (full width when no Time's Up! block),
          full-width underneath when stacked. */}
      <div
        style={{
          flex: horizontal && timesUp ? '2 1 0' : '1 1 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem',
          padding: '1.5rem 1rem',
          background: theme.bg.raised,
          borderRadius: '12px',
        }}
      >
        <div
          style={{
            display: 'flex',
            // Checkbox follows the working hand: 'left' → before the label,
            // 'right' → after it (row-reverse, as TaskRectangle does).
            flexDirection: checkboxSide === 'right' ? 'row-reverse' : 'row',
            alignItems: 'center',
            gap: '0.75rem',
          }}
        >
          <Checkbox
            state={checkboxState}
            label={`Mark ${phaseLabel} complete`}
            onChange={onToggle}
          />
          <span style={{ fontSize: '1.2rem', fontWeight: 700 }}>{phaseLabel} Complete</span>
        </div>
        {!canComplete && (
          <span style={{ fontSize: '0.85rem', color: theme.text.muted }}>
            Check off every task to finish.
          </span>
        )}
      </div>
    </div>
  );
}

export default PhaseEndPanel;
